import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientToPaperPoint } from '@peruselab/structure-editor/components';
import type { ReactionZone } from '@peruselab/structure-editor/domain/reactionGraph';
import { useDropTarget, type DragPayload, type DropPoint } from './DragDrop';
import { isStructurePayload } from './payloads';

export type BoxDropState = 'candidate' | 'over';

/**
 * #699/#700 -- the paper and its four reaction boxes as drop targets. A structure dropped on the
 * paper is centred where it was dropped; dropped in a box, it also joins that box (which grows to
 * hold it). A box is inside the paper, and the innermost target wins, so a drop in a box is the
 * box's. The boxes only register while they are drawn (their elements come from the paper).
 */
export function usePaperDropTargets(clientToPaper: RefObject<ClientToPaperPoint | null>) {
  const { t } = useTranslation();
  const dropAt =
    (zone?: ReactionZone) =>
    (payload: DragPayload, { clientX, clientY }: DropPoint) => {
      const at = clientToPaper.current?.(clientX, clientY);
      if (at && isStructurePayload(payload)) payload.place(zone ? { at, zone } : { at });
    };
  const box = (zone: ReactionZone) => ({
    label: t('dragDrop.box', { box: t(`reactionZones.${zone}`) }),
    accepts: isStructurePayload,
    onDrop: dropAt(zone)
  });

  const paper = useDropTarget({ label: t('dragDrop.paper'), accepts: isStructurePayload, onDrop: dropAt() });
  // One hook per box, in a fixed order (REACTION_ZONES).
  const boxes = {
    reactants: useDropTarget(box('reactants')),
    products: useDropTarget(box('products')),
    above: useDropTarget(box('above')),
    below: useDropTarget(box('below'))
  } satisfies Record<ReactionZone, unknown>;
  const state = (zone: ReactionZone): BoxDropState | undefined =>
    boxes[zone].isOver ? 'over' : boxes[zone].canDrop ? 'candidate' : undefined;

  return {
    paper,
    /** The box elements' refs -- stable (state setters), so passing them re-registers nothing. */
    boxRefs: {
      reactants: boxes.reactants.setNode,
      products: boxes.products.setNode,
      above: boxes.above.setNode,
      below: boxes.below.setNode
    },
    boxStates: {
      reactants: state('reactants'),
      products: state('products'),
      above: state('above'),
      below: state('below')
    }
  };
}
